import { AssetAllocation, GrowthAllocationType, Strategy, StrategyProvider, StrategyType } from '@ndvr/portfolio-api';

/** A Strategy being cloned into a new one — everything but the identity. See AllocationDraft. */
export type StrategyDraft = Omit<Strategy, 'strategyGUID'> & { strategyGUID?: undefined };

// MQV is used by one account group but must not be offered as a selectable strategy for now.
const MQV_STRATEGY_GUID = '052089ee-025e-43d5-822d-2cf14d113555';

export const isSelectableStrategy = (strategy: Pick<Strategy, 'strategyGUID'>): boolean =>
    strategy.strategyGUID !== MQV_STRATEGY_GUID;

/** A BlackRock model strategy: a shared strategy authored and managed by BlackRock. */
export const isBlackrockStrategy = (strategy: Pick<Strategy, 'provider'>): boolean =>
    strategy.provider === StrategyProvider.BLACKROCK;

export type StrategyBadgeTone = 'claro' | 'blackrock';

export interface StrategyBadge {
    label: string;
    tone: StrategyBadgeTone;
}

/** The "who manages this" badge shown next to a strategy; undefined for user-created strategies. */
export const getStrategyTypeBadge = (
    strategy: Pick<Strategy, 'strategyType' | 'provider'>,
): StrategyBadge | undefined => {
    if (strategy.strategyType === StrategyType.CORE) {
        return { label: 'Claro', tone: 'claro' };
    }
    if (isBlackrockStrategy(strategy)) {
        return { label: 'BlackRock', tone: 'blackrock' };
    }
    return undefined;
};

/** Label for the strategy's type, e.g. "Shared (BlackRock)". */
export const getStrategyTypeLabel = (strategy: Pick<Strategy, 'strategyType' | 'provider'>): string => {
    switch (strategy.strategyType) {
        case StrategyType.CORE:
            return 'Core';
        case StrategyType.PERSONAL:
            return 'Personal';
        case StrategyType.TENANT:
            return 'Client base'; // TODO show the actual tenant name here in the future
        case StrategyType.SHARED:
            return isBlackrockStrategy(strategy) ? 'Shared (BlackRock)' : 'Shared';
    }
};

export const getHoldingsCountLabel = (holdingsCount: number): string =>
    `${holdingsCount} ${holdingsCount === 1 ? 'holding' : 'holdings'}`;

export const getStrategyHoldingsLabel = (
    strategy: Pick<Strategy, 'growthAllocationType' | 'assetAllocations'>,
): string => {
    if (strategy.growthAllocationType === GrowthAllocationType.US_DIRECT_STOCKS) {
        return 'Typical holdings vary by account';
    }

    return getHoldingsCountLabel(strategy.assetAllocations.length);
};

/**
 * Extracts and deduplicates security IDs from asset allocations.
 * Includes both primary assets and their replacement assets.
 */
export function extractSecurityIdentifiers(allocations: AssetAllocation[]): string[] {
    const sIds = allocations.flatMap((allocation) => [
        allocation.securityId,
        ...(allocation.replacementAssets || []).map((replacement) => replacement.securityId),
    ]);

    return Array.from(new Set(sIds));
}
